import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import { Link, useNavigate } from 'react-router';
import Pagination from '../../components/CustomPagination';
import {
  GetUserIncidentsResponse,
  IncidentReportingApiIncidentReportingControllerGetUserIncidentsRequest,
  Incidents,
  IncidentsSeverityEnum,
  IncidentsStatusEnum,
} from '@EHSNavigator/backend';
import moment from 'moment';
import { useDebounce } from '@uidotdev/usehooks';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import NoItemsFound from '../../components/NoItemsFound';
import Loader from '../../components/Loader';
import {
  handleExportToCSV,
  handleExportToExcel,
} from '../../utils/exportTable';
import Button from '../../components/Button';
import { useClickOutside } from '../../utils/clickOutSide';

interface MyReportedIncidentsProps {
  otherClasses?: string;
  params: IncidentReportingApiIncidentReportingControllerGetUserIncidentsRequest;
  departments: {
    label: string;
    value: string;
  }[];
  incidentTypes: {
    label: string;
    value: string;
  }[];
  data: GetUserIncidentsResponse;
  noDataMessage: string;
  isIncidentsLoading: boolean;
}

const MyReportedIncidents: React.FC<MyReportedIncidentsProps> = ({
  otherClasses,
  params,
  departments,
  data,
  incidentTypes,
  noDataMessage,
  isIncidentsLoading,
}) => {
  const myReportedIncidentsClasses = clsx(
    otherClasses,
    'flex flex-col w-full flex-1',
  );
  const [toggle, setToggle] = useState(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });
  const navigate = useNavigate();
  const [state, setState] = useState({
    page: Number(params?.page),
    search: params?.search || '',
    status: params?.status || '',
    types: params?.incidentTypeId || '',
    severity: params?.severity || '',
    department: params?.departmentId || '',
    limit: params?.limit || 10,
  });
  const debouncedSearch = useDebounce(state?.search, 600);
  const dispatch = useDispatch();
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      status: state.status,
      type: state.types,
      severity: state.severity,
      department: state.department,
      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/my-reported-incidents?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.status,
    state.types,
    state.severity,
    state.department,
    state.page,
    state.limit,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/my-reported-incidents?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const exportData = {
    header: [
      'Sr.',
      'Ref#',
      'Incident/Hazard',
      'Date of Report',
      'Type',
      'Severity',
      'Status',
    ],
    columnWidths: [
      { width: 5 },
      { width: 18 },
      { width: 30 },
      { width: 20 },
      { width: 30 },
      { width: 15 },
      { width: 15 },
    ],
    exportData: data?.incidents?.map((incident: any, index: number) => {
      return {
        'Sr.': (state.page - 1) * state.limit + index + 1,
        'Ref#': incident.incidentRefId,
        'Incident/Hazard': incident.title
          ? incident.title
              .split(' ')
              .map(
                (word: string) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(' ')
          : '',
        'Date of Report': moment(incident.reportedAt).format('YYYY-M-D'),
        Type: incident.incidentType?.name || '',
        Severity: incident.severity,
        Status: incident.status,
      };
    }),
  };

  const options = [
    {
      label: 'Export CSV',
      onClick: () => {
        handleExportToCSV(exportData?.exportData, exportData?.header);
      },
    },
    {
      label: 'Export XLSX',
      onClick: () => {
        handleExportToExcel(
          exportData?.exportData,
          exportData?.header,
          exportData?.columnWidths,
        );
      },
    },
  ];

  return (
    <div
      className={myReportedIncidentsClasses}
      data-testid="my-reported-incidents"
    >
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">My Reported Incidents</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            All incidents that have been reported by you are listed here.
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        All incidents that have been reported by you are listed here.
      </p>
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <div className="flex items-stretch gap-2.5 lg:mt-12">
        <SearchInput
          name="search"
          onChange={(e) =>
            setState((prev) => ({
              ...prev,
              search: e?.target?.value,
            }))
          }
          placeholder="Enter title, ID, or department..."
          value={state?.search}
          disabled={false}
          errorMessage=""
          otherClasses={{ container: 'w-full ' }}
        />
        <div ref={ref} className="relative flex lg:block">
          <Button
            onClick={() => setToggle(!toggle)}
            otherClasses="!py-0 flex-1 h-full"
            size="smaller"
            disable={data?.incidents?.length === 0}
          >
            <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
            <span className="hidden md:block">Export</span>
          </Button>
          {toggle && (
            <ul
              className={clsx(
                'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-14 right-0 z-10 flex w-fit flex-col rounded-md p-2.5 ring md:w-full lg:top-18',
              )}
            >
              {options.map((option, index) => (
                <button
                  key={index}
                  className={clsx(
                    'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                  )}
                  onClick={() => option.onClick()}
                >
                  {option.label}
                </button>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="global-scrollbar-style flex-1 overflow-auto px-0.5">
        <div className="flex w-full min-w-6xl flex-col">
          <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
            <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
              <button
                onClick={() => {
                  if (
                    state?.search !== '' ||
                    state?.department !== '' ||
                    state?.status !== '' ||
                    state?.types !== '' ||
                    state?.severity !== '' ||
                    state?.limit !== 10
                  ) {
                    setState((prev) => ({
                      ...prev,
                      search: '',
                      department: '',
                      status: '',
                      types: '',
                      severity: '',
                      limit: 10,
                    }));
                  }
                }}
                className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
              >
                {state?.department !== '' ||
                state?.status !== '' ||
                state?.severity !== '' ||
                state?.types !== '' ||
                state?.search !== '' ||
                state?.limit !== 10 ? (
                  <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                ) : (
                  <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                )}
                Filters
              </button>
            </p>
            <div className="flex flex-1 items-center gap-6">
              <Select
                filterSelect
                list={Object.values(IncidentsStatusEnum)
                  .filter((status) => status !== IncidentsStatusEnum.Draft)
                  .map((status) => ({
                    label: status,
                    value: status,
                  }))}
                value={state?.status}
                placeholder="Status"
                otherClasses={{
                  container: 'w-full max-w-34',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    status: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={departments}
                value={state?.department}
                placeholder="Departments"
                otherClasses={{ container: 'w-full max-w-48' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    department: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={incidentTypes}
                value={state?.types}
                placeholder="Incident Types"
                otherClasses={{ container: 'w-full max-w-46' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    types: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.entries(IncidentsSeverityEnum).map(
                  ([, value]) => ({
                    label: value,
                    value,
                  }),
                )}
                value={state?.severity}
                placeholder="Severities"
                otherClasses={{ container: 'w-full max-w-36' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    severity: e,
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
        </div>
        {isIncidentsLoading ? (
          <div>
            <Loader
              variant="compact"
              otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
            />
          </div>
        ) : (
          <div className="w-full min-w-6xl">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Ref#
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:text-lg lg:leading-5">
                Incident/Hazard
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Date of Report
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Type
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Severity
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Status
              </h6>
            </div>

            {data?.incidents?.length > 0 ? (
              <>
                {data?.incidents?.map((incident: Incidents, index: number) => {
                  return (
                    <Link
                      to={'/my-reported-incidents/' + incident.id}
                      key={index}
                      className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                    >
                      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                        {(state.page - 1) * state.limit + index + 1}
                      </p>
                      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input lg:leading-5` col-span-2 text-sm leading-4 font-normal uppercase first-letter:uppercase lg:text-base">
                        {incident.incidentRefId}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 text-sm leading-4 font-normal lowercase first-letter:uppercase lg:text-base lg:leading-5">
                        {incident.title}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                        {moment(incident.reportedAt).format('YYYY-MM-DD')}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                        {incident.incidentType?.name || ''}
                      </p>
                      <p
                        className={clsx(
                          'font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lg:text-base lg:leading-5',
                          incident.severity === IncidentsSeverityEnum.Critical
                            ? 'text-brand-posh-pink'
                            : incident.severity === IncidentsSeverityEnum.Major
                              ? 'text-brand-fair-orange'
                              : incident.severity ===
                                  IncidentsSeverityEnum.Moderate
                                ? 'text-primary-yellow'
                                : 'text-brand-papery',
                        )}
                      >
                        {incident.severity}
                      </p>
                      <p
                        className={clsx(
                          'font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold whitespace-nowrap lg:text-base lg:leading-5',
                          incident.status === IncidentsStatusEnum?.Active
                            ? 'text-brand-posh-pink'
                            : incident.status === IncidentsStatusEnum?.Closed
                              ? 'text-brand-papery'
                              : 'text-brand-byzantine',
                        )}
                      >
                        {incident.status}
                      </p>
                    </Link>
                  );
                })}
              </>
            ) : (
              <NoItemsFound message={noDataMessage} />
            )}
          </div>
        )}
      </div>
      {data?.incidents?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.incidents?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          otherClasses=""
        />
      )}
    </div>
  );
};

export default MyReportedIncidents;
